import React, { useState } from 'react';
import { getWebsiteUnits, getContact, isSold, getUnitName, getPageContent } from './websiteStyles';

const DARK = '#14161C';
const PAPER = '#FAFAF8';
const PAPER2 = '#F3F3F1';
const INK = '#14161C';
const INK2 = '#52545C';
const INK3 = '#8B8D94';
const INK4 = '#B0B2B8';
const LINE = '#E5E5E3';
const BLUE = '#2563EB';
const GREEN = '#22C55E';
const heading = '"Inter Tight", sans-serif';
const mono = '"JetBrains Mono", monospace';
const serif = '"Instrument Serif", serif';

/* Category icon SVGs */
const CatIcon = ({ type }) => {
  const s = { width: 24, height: 24 };
  const icons = {
    cuts: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" style={s}><path d="M6 9a3 3 0 100-6 3 3 0 000 6zM6 21a3 3 0 100-6 3 3 0 000 6zM20 4L8.12 15.88M14.47 14.48L20 20M8.12 8.12L12 12"/></svg>,
    engine: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" style={s}><circle cx="12" cy="12" r="3"/><path d="M12 1v4m0 14v4M4.22 4.22l2.83 2.83m9.9 9.9l2.83 2.83M1 12h4m14 0h4M4.22 19.78l2.83-2.83m9.9-9.9l2.83-2.83"/></svg>,
    drivetrain: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" style={s}><circle cx="6" cy="6" r="3"/><circle cx="18" cy="6" r="3"/><circle cx="6" cy="18" r="3"/><circle cx="18" cy="18" r="3"/><path d="M6 9v9m12-9v9M9 6h6"/></svg>,
    suspension: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" style={s}><path d="M12 2v2m0 16v2M6 8l1.5 2L6 12l1.5 2L6 16m12-8l-1.5 2L18 12l-1.5 2L18 16"/><circle cx="12" cy="12" r="3"/></svg>,
    body: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" style={s}><rect x="3" y="11" width="18" height="7" rx="2"/><path d="M7 11V8a2 2 0 012-2h6a2 2 0 012 2v3"/><circle cx="7.5" cy="18" r="1.5"/><circle cx="16.5" cy="18" r="1.5"/></svg>,
    lights: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" style={s}><circle cx="12" cy="12" r="5"/><path d="M12 1v2m0 18v2M4.22 4.22l1.42 1.42m12.72 12.72l1.42 1.42M1 12h2m18 0h2M4.22 19.78l1.42-1.42M18.36 5.64l1.42-1.42"/></svg>,
    cooling: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" style={s}><path d="M12 2v20M2 12h20M4.93 4.93l14.14 14.14M19.07 4.93L4.93 19.07"/></svg>,
    interior: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" style={s}><path d="M7 18v-6a5 5 0 0110 0v6"/><path d="M5 18h14v2a2 2 0 01-2 2H7a2 2 0 01-2-2v-2z"/><path d="M9 6V4a3 3 0 016 0v2"/></svg>,
  };
  return icons[type] || icons.engine;
};

function WebsiteSpareParts({ onViewUnit }) {
  const isMobile = typeof window !== 'undefined' && window.innerWidth < 600;
  const cms = getPageContent('website_parts') || {};
  const [searchMake, setSearchMake] = useState('');
  const [searchModel, setSearchModel] = useState('');
  const [searchYear, setSearchYear] = useState('');
  const [searchText, setSearchText] = useState('');
  const [activeCat, setActiveCat] = useState('');

  const contact = getContact();
  const allUnits = getWebsiteUnits();
  const parts = allUnits.filter(u => u._type === 'part' || u._type === 'engine' || (u._type === 'vehicle' && u.cutType));

  const makes = [...new Set(parts.map(u => u.make).filter(Boolean))].sort();
  const models = searchMake ? [...new Set(parts.filter(u => u.make === searchMake).map(u => u.model).filter(Boolean))].sort() : [];
  const years = [...new Set(parts.map(u => u.year).filter(Boolean))].sort((a, b) => b - a);

  // Resolve actual category for each part (handles "Cut Types"/"Cut Unit" → specific name)
  const resolvePartCat = (p) => {
    if (p.category === 'Cut Types' || p.category === 'Cut Unit') {
      return (p.partName?.split('—').pop()?.trim() || p.cutType || p.category).toLowerCase();
    }
    return (p.category || p.partName || p.engineName || '').toLowerCase();
  };

  const catDefs = [
    { name: 'Half Cut', key: 'half cut|half-cut|h/cut', icon: 'cuts' },
    { name: 'Nose Cut', key: 'nose cut|nose-cut', icon: 'cuts' },
    { name: 'Engines', key: 'engine', icon: 'engine' },
    { name: 'Axles', key: 'axle|axel|drivetrain', icon: 'drivetrain' },
    { name: 'CKD', key: 'ckd', icon: 'cuts' },
    { name: 'SKD', key: 'skd', icon: 'cuts' },
    { name: 'Suspension', key: 'suspension|steering|strut', icon: 'suspension' },
    { name: 'Body Panels', key: 'body panel|fender|bumper|bonnet|hood', icon: 'body' },
    { name: 'Lights', key: 'light|headl|tail l|fog', icon: 'lights' },
    { name: 'Cooling & AC', key: 'cool|radiator|condenser|a/c', icon: 'cooling' },
    { name: 'Interior', key: 'interior|seat|dashboard|console', icon: 'interior' },
  ];

  const partCats = catDefs.map(c => {
    const keys = c.key.split('|');
    return {
      ...c,
      count: parts.filter(p => {
        const cat = resolvePartCat(p);
        return keys.some(k => cat.includes(k));
      }).length,
    };
  }).filter(c => c.count > 0);

  // Filter parts
  let filtered = parts;
  if (activeCat) {
    const keys = activeCat.split('|');
    filtered = filtered.filter(p => {
      const cat = resolvePartCat(p);
      return keys.some(k => cat.includes(k));
    });
  }
  if (searchMake) filtered = filtered.filter(p => p.make === searchMake);
  if (searchModel) filtered = filtered.filter(p => p.model === searchModel);
  if (searchYear) filtered = filtered.filter(p => String(p.year) === searchYear);
  if (searchText) {
    const q = searchText.toLowerCase();
    filtered = filtered.filter(p => [p.partName, p.engineName, p.sku, p.make, p.model, p.category].filter(Boolean).join(' ').toLowerCase().includes(q));
  }

  const selectStyle = { height: 44, padding: '0 14px', borderRadius: 8, border: 'none', background: 'transparent', fontFamily: heading, fontSize: 14, color: PAPER, outline: 'none', cursor: 'pointer', appearance: 'none', WebkitAppearance: 'none', width: '100%' };

  return (
    <div>
      {/* ═══ Dark Hero ═══ */}
      <section style={{ background: DARK, color: PAPER, position: 'relative', overflow: 'hidden' }}>
        <div style={{ position: 'absolute', inset: 0, opacity: 0.03, pointerEvents: 'none', backgroundImage: `linear-gradient(rgba(250,250,248,0.5) 1px, transparent 1px), linear-gradient(90deg, rgba(250,250,248,0.5) 1px, transparent 1px)`, backgroundSize: '80px 80px' }} />
        <div style={{ maxWidth: 1440, margin: '0 auto', padding: '80px 24px 60px', position: 'relative' }}>
          <div style={{ fontFamily: mono, fontSize: 10, letterSpacing: '0.14em', textTransform: 'uppercase', color: 'rgba(250,250,248,0.35)', marginBottom: 20, display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ width: 5, height: 5, borderRadius: '50%', background: GREEN }}></span>
            {cms.hero?.eyebrow || 'Spare Parts Catalog'}
          </div>
          {(() => {
            const hl = cms.hero?.headline || 'Genuine parts. Fit-checked. In stock.';
            const accent = cms.hero?.headline_accent_word || 'In stock.';
            const pts = hl.split(accent);
            return (
              <h1 style={{ fontFamily: heading, fontWeight: 700, letterSpacing: '-0.04em', fontSize: 'clamp(36px, 6vw, 64px)', lineHeight: 0.95, maxWidth: 600, marginBottom: 20 }}>
                {pts[0]}
                <span style={{ fontFamily: serif, fontStyle: 'italic', color: '#60A5FA', fontWeight: 400 }}>{accent}</span>
                {pts[1] || ''}
              </h1>
            );
          })()}
          <p style={{ fontSize: 15, color: 'rgba(250,250,248,0.4)', maxWidth: 460, lineHeight: 1.7, marginBottom: 40 }}>
            {cms.hero?.subheading || 'Find the right part for your vehicle. Search by make, model & year — quote in 24 hours.'}
          </p>

          {/* Search bar */}
          <div style={{ background: 'rgba(255,255,255,0.06)', borderRadius: 16, border: '1px solid rgba(255,255,255,0.08)', padding: '12px 16px', display: 'flex', alignItems: 'center', gap: 0, maxWidth: 700, flexWrap: 'wrap' }}>
            <div style={{ width: 36, height: 36, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
              <svg viewBox="0 0 24 24" fill="none" stroke="rgba(250,250,248,0.3)" strokeWidth="2" style={{ width: 18, height: 18 }}><circle cx="11" cy="11" r="8"/><path d="M21 21l-4.35-4.35"/></svg>
            </div>
            <div style={{ flex: '1 1 120px', borderRight: '1px solid rgba(255,255,255,0.06)', padding: '0 12px' }}>
              <div style={{ fontFamily: mono, fontSize: 9, letterSpacing: '0.12em', textTransform: 'uppercase', color: 'rgba(250,250,248,0.25)', marginBottom: 2 }}>Make</div>
              <select value={searchMake} onChange={e => { setSearchMake(e.target.value); setSearchModel(''); }} style={{ ...selectStyle, color: searchMake ? PAPER : 'rgba(250,250,248,0.4)' }}>
                <option value="">Any</option>
                {makes.map(m => <option key={m} value={m}>{m}</option>)}
              </select>
            </div>
            <div style={{ flex: '1 1 120px', borderRight: '1px solid rgba(255,255,255,0.06)', padding: '0 12px' }}>
              <div style={{ fontFamily: mono, fontSize: 9, letterSpacing: '0.12em', textTransform: 'uppercase', color: 'rgba(250,250,248,0.25)', marginBottom: 2 }}>Model</div>
              <select value={searchModel} onChange={e => setSearchModel(e.target.value)} style={{ ...selectStyle, color: searchModel ? PAPER : 'rgba(250,250,248,0.4)' }} disabled={!searchMake}>
                <option value="">Any</option>
                {models.map(m => <option key={m} value={m}>{m}</option>)}
              </select>
            </div>
            <div style={{ flex: '1 1 80px', padding: '0 12px' }}>
              <div style={{ fontFamily: mono, fontSize: 9, letterSpacing: '0.12em', textTransform: 'uppercase', color: 'rgba(250,250,248,0.25)', marginBottom: 2 }}>Year</div>
              <select value={searchYear} onChange={e => setSearchYear(e.target.value)} style={{ ...selectStyle, color: searchYear ? PAPER : 'rgba(250,250,248,0.4)' }}>
                <option value="">Any</option>
                {years.map(y => <option key={y} value={y}>{y}</option>)}
              </select>
            </div>
            <button onClick={() => {}} style={{ flexShrink: 0, display: 'flex', alignItems: 'center', gap: 6, padding: '10px 22px', borderRadius: 10, background: BLUE, color: '#fff', fontFamily: heading, fontSize: 14, fontWeight: 600, border: 'none', cursor: 'pointer', marginLeft: 8 }}>
              {cms.hero?.cta_text || 'Find parts →'}
            </button>
          </div>
        </div>
      </section>

      {/* ═══ Browse Categories ═══ */}
      <section style={{ background: PAPER2, borderBottom: `1px solid ${LINE}` }}>
        <div style={{ maxWidth: 1440, margin: '0 auto', padding: '32px 24px' }}>
          <div style={{ fontFamily: mono, fontSize: 10, letterSpacing: '0.14em', textTransform: 'uppercase', color: INK3, marginBottom: 16 }}>Browse Categories</div>
          <div style={{ display: 'flex', gap: 10, overflowX: 'auto', paddingBottom: 4 }}>
            {partCats.map(cat => (
              <button key={cat.name} onClick={() => setActiveCat(activeCat === cat.key ? '' : cat.key)}
                style={{
                  flexShrink: 0, display: 'flex', alignItems: 'center', gap: 10, padding: '12px 18px', borderRadius: 12,
                  border: `1px solid ${activeCat === cat.key ? BLUE : LINE}`,
                  background: activeCat === cat.key ? '#EFF6FF' : '#fff',
                  cursor: 'pointer', transition: 'all 0.2s', fontFamily: heading,
                }}>
                <div style={{ color: activeCat === cat.key ? BLUE : INK }}><CatIcon type={cat.icon} /></div>
                <div style={{ textAlign: 'left' }}>
                  <div style={{ fontSize: 13, fontWeight: 600, color: INK }}>{cat.name}</div>
                  <div style={{ fontFamily: mono, fontSize: 10, color: INK4, marginTop: 1 }}>{cat.count} skus</div>
                </div>
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* ═══ Parts Grid ═══ */}
      <section style={{ maxWidth: 1440, margin: '0 auto', padding: '40px 24px 80px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24, flexWrap: 'wrap', gap: 12 }}>
          <h2 style={{ fontFamily: heading, fontWeight: 700, letterSpacing: '-0.03em', fontSize: 28, color: INK }}>
            All parts <span style={{ fontWeight: 400, color: INK3 }}>· {filtered.length}</span>
          </h2>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <div style={{ position: 'relative' }}>
              <input value={searchText} onChange={e => setSearchText(e.target.value)} placeholder="Search SKU or part name..."
                style={{ height: 38, padding: '0 12px 0 12px', borderRadius: 8, border: `1px solid ${LINE}`, background: '#fff', fontFamily: heading, fontSize: 13, color: INK, outline: 'none', width: 220 }} />
            </div>
            {(activeCat || searchMake || searchText) && (
              <button onClick={() => { setActiveCat(''); setSearchMake(''); setSearchModel(''); setSearchYear(''); setSearchText(''); }}
                style={{ padding: '8px 14px', borderRadius: 8, border: `1px solid ${LINE}`, background: '#fff', fontFamily: heading, fontSize: 13, fontWeight: 500, color: INK, cursor: 'pointer' }}>
                Reset
              </button>
            )}
          </div>
        </div>

        {filtered.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '60px 0', color: INK3 }}>
            <div style={{ fontSize: 40, marginBottom: 12 }}>🔧</div>
            <div style={{ fontFamily: heading, fontSize: 18, fontWeight: 600, color: INK, marginBottom: 6 }}>No parts found</div>
            <div style={{ fontSize: 14 }}>Try adjusting your filters or search.</div>
          </div>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fill, minmax(300px, 1fr))', gap: 16 }}>
            {filtered.map(p => {
              const name = p.partName || p.engineName || getUnitName(p);
              const sold = isSold(p);
              const img = p.images?.[0];
              const cat = p.category || (p._type === 'engine' ? 'Engine' : 'Part');
              const brand = p.brand || p.make || '';
              const fits = p.make && p.model ? `${p.make} ${p.model}${p.year ? ' ' + p.year : ''}` : '';
              const stock = sold ? 0 : (p.quantity || 1);
              const waMsg = encodeURIComponent(`Hi, I need a quote for: ${name}${p.sku ? ' (SKU: ' + p.sku + ')' : ''}${fits ? ', fits ' + fits : ''}. Please advise availability and pricing.`);

              return (
                <div key={p.id} onClick={() => onViewUnit?.(p)} style={{ background: '#fff', borderRadius: 12, overflow: 'hidden', border: `1px solid ${LINE}`, transition: 'box-shadow 0.2s, transform 0.2s', cursor: 'pointer' }}
                  onMouseEnter={e => { e.currentTarget.style.boxShadow = '0 6px 24px rgba(0,0,0,0.07)'; e.currentTarget.style.transform = 'translateY(-1px)'; }}
                  onMouseLeave={e => { e.currentTarget.style.boxShadow = 'none'; e.currentTarget.style.transform = ''; }}>
                  {/* Image */}
                  <div style={{ aspectRatio: '4/3', background: PAPER2, display: 'flex', alignItems: 'center', justifyContent: 'center', position: 'relative', overflow: 'hidden' }}>
                    {img ? (
                      <img src={img} alt={name} loading="lazy" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                    ) : (
                      <div style={{ fontFamily: mono, fontSize: 11, letterSpacing: '0.1em', textTransform: 'uppercase', color: INK4 }}>
                        Part · {cat}
                      </div>
                    )}
                  </div>
                  {/* Content */}
                  <div style={{ padding: '14px 16px 16px' }}>
                    {/* Badges */}
                    <div style={{ display: 'flex', gap: 6, marginBottom: 10, flexWrap: 'wrap' }}>
                      <span style={{ padding: '3px 8px', borderRadius: 4, background: INK, color: '#fff', fontFamily: mono, fontSize: 9, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase' }}>Genuine</span>
                      {brand && <span style={{ padding: '3px 8px', borderRadius: 4, background: PAPER2, color: INK2, fontFamily: mono, fontSize: 9, fontWeight: 600, letterSpacing: '0.06em', textTransform: 'uppercase' }}>{brand}</span>}
                    </div>
                    {/* Name */}
                    <div style={{ fontFamily: heading, fontSize: 16, fontWeight: 600, color: INK, marginBottom: 6, lineHeight: 1.3 }}>{name}</div>
                    {/* SKU */}
                    {p.sku && <div style={{ fontFamily: mono, fontSize: 11, color: INK3, marginBottom: 4, letterSpacing: '0.04em' }}>SKU: {p.sku}</div>}
                    {/* Fits */}
                    {fits && <div style={{ fontSize: 12, color: INK3, marginBottom: 12 }}>Fits: {fits}</div>}
                    {/* Bottom row */}
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', paddingTop: 12, borderTop: `1px solid ${LINE}` }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontFamily: mono, fontSize: 11, color: stock > 0 ? '#16A34A' : '#DC2626' }}>
                        <span style={{ width: 7, height: 7, borderRadius: '50%', background: stock > 0 ? GREEN : '#EF4444' }}></span>
                        {stock > 0 ? `${stock} in stock` : 'Out of stock'}
                      </div>
                      {stock > 0 && (
                        <a href={`https://wa.me/${contact.whatsapp}?text=${waMsg}`} target="_blank" rel="noopener noreferrer"
                          style={{ display: 'inline-flex', alignItems: 'center', gap: 5, padding: '6px 14px', borderRadius: 9999, background: GREEN, color: '#fff', fontSize: 12, fontWeight: 600, textDecoration: 'none' }}>
                          <span style={{ width: 6, height: 6, borderRadius: '50%', background: '#fff', opacity: 0.7 }}></span> Quote
                        </a>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </section>
    </div>
  );
}

export default WebsiteSpareParts;
